import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { GenericErrorModalService } from '@services/generic-error/generic-error.service';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { select, Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { getRtkJwt, UpdateGWCookie, UpdateRtkJwt, UpdateTkJwt } from '@store/flow-data/general';
import { RefreshLoginReset, RefreshLoginService, selectRefreshLoginState } from '@store/swagger/authentications';
import { isNotNullish } from '@global/utils';
import { HeadersEnum } from '@services/api/api.service';
import { HEADER_GWCOOKIE, HEADER_X_REFRESH_TOKEN, HEADER_X_TOKEN } from '@global/constants';

@Injectable()
export class JwtInterceptorService implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private isSessionExpired = false;

  // Refresh Token Subject tracks the current token, or is null if no token is currently
  private refreshToken: string;

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly genericErrorModalService: GenericErrorModalService,
    private readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly refreshLoginService: RefreshLoginService
  ) {
    this.store
      .pipe(select(getRtkJwt))
      .pipe(filter(refreshToken => isNotNullish(refreshToken)))
      .subscribe((refreshToken: string) => {
        this.refreshToken = refreshToken;
      });
  }

  // tslint:disable-next-line: no-any
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap(response => {
        if (response instanceof HttpResponse) {
          const token = response.headers.get(HEADER_X_TOKEN);
          const refreshToken = response.headers.get(HEADER_X_REFRESH_TOKEN);
          const gwCookie = response.headers.get(HEADER_GWCOOKIE);
          if (token && refreshToken) {
            this.store.dispatch(UpdateTkJwt({ data: token }));
            this.store.dispatch(UpdateRtkJwt({ data: refreshToken }));
          }
          if (gwCookie) {
            this.store.dispatch(UpdateGWCookie({ data: gwCookie }));
          }
          this.analyticsService.setErrorMessage('');
          this.analyticsService.setErrorType('');
        }
      }),
      catchError(err => {
        // If error status is different than 401 we want to skip refresh token
        // So we check that and throw the error if it's the case
        if (this.isSessionExpired || err.status !== 401) {
          const errorMessage = `${request.method}:${err.url}:${err.status}`;
          if (this.router.url !== '/' && err.status >= 500) {
            this.genericErrorModalService.showUnexpectedErrorModal(errorMessage, 'service');
          } else {
            this.analyticsService.setErrorMessage(errorMessage);
            this.analyticsService.setErrorType('integration');
          }
          return throwError(err);
        }

        // We don't want to refresh token for some requests like login or refresh token itself
        if (request.url.includes('refreshLogin') && this.refreshTokenInProgress) {
          this.isSessionExpired = true;
          return throwError(err);
        }

        if (this.refreshTokenInProgress) {
          // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
          // – which means the new token is ready and we can retry the request again

          return this.store.pipe(select(selectRefreshLoginState)).pipe(
            filter(({ loading, data }) => loading !== null && !loading && data !== null),
            switchMap(() => {
              if (this.isSessionExpired) {
                return throwError(err);
              }
              return next.handle(
                request.clone({
                  setHeaders: {
                    [HeadersEnum.AUTHORIZATION]: `Bearer ${this.refreshToken}`,
                  },
                })
              );
            })
          );
        }
        this.refreshTokenInProgress = true;
        // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
        this.store.dispatch(RefreshLoginReset());
        // Update temporally JWT with refresh token because headers will inject JWT Token
        this.store.dispatch(UpdateTkJwt({ data: this.refreshToken }));

        return this.refreshLoginService.refreshLogin({}).pipe(
          switchMap(data => {
            if (data) {
              // Update JWT with new token
              this.store.dispatch(UpdateTkJwt({ data: data.token })), (this.refreshTokenInProgress = false);
              return next.handle(
                request.clone({
                  setHeaders: {
                    [HeadersEnum.AUTHORIZATION]: `Bearer ${data.token}`,
                  },
                })
              );
            }
            return undefined;
          }),
          catchError(err => {
            this.refreshTokenInProgress = false;
            return throwError(err);
          })
        );
      })
    );
  }
}
